import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { LANGUAGE_CODES, DEFAULT_LANGUAGE } from './languages';

// Static imports so all locale bundles ship with the build (self-hosted, no CDN
// dependency). English is the source of truth and the fallback — any key missing
// in another language renders the English string instead of a blank/broken label.
import en from './locales/en.json';
import ptBR from './locales/pt-BR.json';
import ru from './locales/ru.json';
import zhCN from './locales/zh-CN.json';
import tr from './locales/tr.json';
import pl from './locales/pl.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import uk from './locales/uk.json';
import ja from './locales/ja.json';
import ro from './locales/ro.json';
import is from './locales/is.json';
import hi from './locales/hi.json';

// Map a detected/stored language code to one of our supported codes. Handles the
// case where the browser reports a base code ('pt', 'zh') but we only ship
// region-coded bundles ('pt-BR', 'zh-CN') — without this the base code wouldn't
// match any bundle and would fall back to English.
function normalizeLng(lng) {
  if (!lng) return DEFAULT_LANGUAGE;
  if (LANGUAGE_CODES.includes(lng)) return lng;
  const base = String(lng).split('-')[0].toLowerCase();
  const match = LANGUAGE_CODES.find(c => c.split('-')[0].toLowerCase() === base);
  return match || DEFAULT_LANGUAGE;
}

const resources = {
  en: { translation: en },
  'pt-BR': { translation: ptBR },
  ru: { translation: ru },
  'zh-CN': { translation: zhCN },
  tr: { translation: tr },
  pl: { translation: pl },
  de: { translation: de },
  fr: { translation: fr },
  uk: { translation: uk },
  ja: { translation: ja },
  ro: { translation: ro },
  is: { translation: is },
  hi: { translation: hi },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: LANGUAGE_CODES,
    fallbackLng: DEFAULT_LANGUAGE,
    // Load the EXACT selected code (e.g. 'pt-BR', 'zh-CN') as-is. Without this,
    // i18next strips region subtags to the base ('pt', 'zh') for lookup — but we
    // only ship region-coded bundles, so those languages wrongly fell back to
    // English. 'currentOnly' keeps the full code so the right bundle is used.
    load: 'currentOnly',
    // Show the English source (or the fallback passed to t()) when a key is
    // missing in the active language — never render an empty string.
    returnEmptyString: false,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // Persist the user's choice; localStorage first, then browser language.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'fluxbot_lang',
      caches: ['localStorage'],
      // Map a base browser code (e.g. 'pt', 'zh') to our region-coded bundle.
      convertDetectedLanguage: normalizeLng,
    },
    react: { useSuspense: false },
  });

export default i18n;
